import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { EChipsVariants } from 'components/atoms/Chips';
import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { tasksTableColumns, mapTasksToDataSource, Elabels } from '../../helpers';
import Button from 'components/atoms/Button';
import { EIconTypes } from 'constants/Icons';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { TasksTableCard } from './TasksTableCard';
import { useCase } from '../../hooks';
import { useParams } from 'react-router-dom';
import { Pagination } from 'components/molecules/Pagination';
import { ETaskStatus } from 'constants/Case';
import { FormModal } from 'components/molecules/Modal';
import { colors } from 'theme/colors';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { useLang } from 'models/langContext';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { IEditTaskFormValue, ITaskFormValue } from 'models/cases';
import { Loader } from 'components/atoms/Loader';
import TaskEditForm from './TaskEditForm';
import { useGlobalProperty } from 'models/globalPropertyContext';

const initialFormValues = {
  nameUniqueId: '',
  description: '',
};

interface ITask {
  showAddTaskNote: boolean;
  isAssigned: boolean;
  isReadOnly?: boolean;
}

const Tasks = ({ showAddTaskNote, isAssigned, isReadOnly }: ITask) => {
  const { t } = useTranslations();
  // const navigate = useNavigate();
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const { isDesktop } = useThemeBreakpoint();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const [modalEditTask, setModalEditTask] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [modalAddTask, setModalAddTask] = useState(false);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const { getTasksData, paginatorTasks, tasks, addTask, deleteTask } = useCase();
  const [isLoading, setIsLoading] = useState(true);
  const { selectedLanguage } = useLang();
  const { loadDictionaries, task } = useConfigurationLicence();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.task]);
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      setIsPaginationLoading(true);
      if (!tasks.length) {
        setIsLoading(true);
      }
      await getTasksData(caseId, currentPage, perPage, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  const updateTasksData = async () => {
    await getTasksData(caseId, currentPage, perPage, sort);
  };

  const schema = yup.object({
    nameUniqueId: yup.string().required(t('general.field.validation.required')),
    description: yup.string().required(t('general.field.validation.required')),
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ITaskFormValue>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ITaskFormValue>,
  });

  const viewAction = (id: string) => {
    setDetailTaskId(id);
  };
  const editAction = (id: string) => {
    setDetailTaskId(id);
    setModalEditTask(true);
  };
  const onCloseEditModal = () => {
    setDetailTaskId(null);
    setModalEditTask(false);
  };
  const handleDeleteTask = async () => {
    if (deleteTaskId) {
      await deleteTask(deleteTaskId);
      updateTasksData();
      setDeleteTaskId(null);
    }
  };
  const deleteAction = (id: string) => {
    setDeleteTaskId(id);
  };

  const onSubmit = async (data: ITaskFormValue) => {
    const res = await addTask(caseId, data);
    if (res) {
      setModalAddTask(false);
      reset(initialFormValues);
      updateTasksData();
    }
  };

  const renderStatusFieldColor = (props: ITableDataSource) => {
    switch (props.statusUniqueId) {
      case ETaskStatus.WAITING:
        return EChipsVariants.primary;
      case ETaskStatus.DONE:
        return EChipsVariants.success;
      default:
        return EChipsVariants.warning;
    }
  };

  return (
    <Grid container sx={{ gap: '10px' }}>
      <Grid
        container
        size={{ xs: 12 }}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: 1,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          pl: 2,
        }}
      >
        <Typography variant="body2">
          {t('caseManagement.cases.tasks.description.text')}
        </Typography>
        {showAddTaskNote && (
          <Grid size={{ xs: 12, md: 'auto' }}>
            <Button
              label={t(Elabels.addTask)}
              icon={EIconTypes.plus}
              variant={EButtonVariants.contained}
              onClick={() => setModalAddTask(true)}
              size={EButtonSizes.medium}
              id="tasks_add-button"
              fullWidth
            />
          </Grid>
        )}
      </Grid>

      {detailTaskId && (
        <TaskEditForm
          onClose={onCloseEditModal}
          taskId={detailTaskId}
          canEdit={modalEditTask}
          updateTasksData={updateTasksData}
        />
      )}

      {deleteTaskId && (
        <FormModal
          open={!!deleteTaskId}
          onClickClose={() => {
            setDeleteTaskId(null);
          }}
          title={t(Elabels.deleteTask)}
          titleLeft
          onClickApprove={handleDeleteTask}
          approveButtonLabel={t('general.confirm.label')}
          text={t(Elabels.deleteTaskConfirm)}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormModal
          open={!!modalAddTask}
          onClickClose={() => {
            setModalAddTask(false);
            reset(initialFormValues);
          }}
          title={t(Elabels.addTask)}
          onClickApprove={handleSubmit(onSubmit)}
        >
          <Grid container sx={{ gap: 4 }}>
            <Grid container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <FormSelect
                  options={mapDictionaryToOptionProp(task)}
                  name="nameUniqueId"
                  setValue={setValue}
                  canBeEmpty
                  label={t(Elabels.taskName)}
                  control={control}
                  errors={errors}
                  required
                  id="tasks_name_unique_id-select"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormInput
                  name="description"
                  type={EFormInputTypes.textarea}
                  minRows={5}
                  multiline
                  label={t(Elabels.taskDescription)}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  charsLimit={255}
                  showCounter
                  required
                  id="tasks_description-input"
                />
              </Grid>
            </Grid>
          </Grid>
        </FormModal>
      </form>
      <Grid size={{ xs: 12 }}>
        {isLoading ? (
          <Loader isLoading />
        ) : (
          <>
            {isDesktop ? (
              <Table
                columns={tasksTableColumns({
                  viewAction,
                  editAction,
                  deleteAction,
                  renderStatusFieldColor,
                  isAssigned,
                  isReadOnly,
                })}
                dataSource={mapTasksToDataSource(tasks, longDateFormat)}
                onSort={setSort}
                id="tasks_list-table"
              />
            ) : (
              <CardList
                items={mapTasksToDataSource(tasks, longDateFormat)}
                render={(data, index) => (
                  <TasksTableCard
                    data={data}
                    viewAction={viewAction}
                    deleteAction={deleteAction}
                    editAction={editAction}
                    renderStatusFieldColor={renderStatusFieldColor}
                    isReadOnly={isReadOnly}
                    key={index}
                  />
                )}
              />
            )}
            {paginatorTasks && (
              <Pagination
                count={paginatorTasks.totalElements}
                onChangePage={(page) => setCurrentPage(page)}
                current={currentPage}
                perPage={perPage}
                onChangePerPage={(value) => setPerPage(value)}
                isLoading={isPaginationLoading}
              />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default Tasks;
